import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { AlertCircle, CheckCircle2, RefreshCw, Users } from 'lucide-react';

interface UserTierData {
  id: number;
  user_id: number;
  tier_level: number;
  tier_name: string;
  trial_start_date: string;
  trial_expires_at: string;
  trial_used: boolean;
  assigned_at: string;
}

const TrialStatusFixer: React.FC = () => {
  const [users, setUsers] = useState<UserTierData[]>([]);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixedCount, setFixedCount] = useState(0);
  const { toast } = useToast();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await window.ezsite.apis.tablePage(15216, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: [
        { name: 'tier_level', op: 'Equal', value: 1 }]

      });

      if (error) throw error;
      setUsers(data?.List || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch user data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const isIncorrectDate = (dateString: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const year1970 = new Date('1970-01-01');
    const year2000 = new Date('2000-01-01');
    return date < year2000; // Dates before 2000 are likely incorrect
  };

  const getProblematicUsers = () => {
    return users.filter((user) =>
    isIncorrectDate(user.trial_start_date) ||
    isIncorrectDate(user.trial_expires_at) ||
    user.trial_start_date && user.trial_expires_at && !user.trial_used
    );
  };

  const fixUserTrialStatus = async (user: UserTierData) => {
    try {
      const updateData = {
        ID: user.id,
        trial_start_date: '',
        trial_expires_at: '',
        trial_used: false
      };

      const { error } = await window.ezsite.apis.tableUpdate(15216, updateData);
      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error fixing user:', user.id, error);
      return false;
    }
  };

  const fixAllProblematicUsers = async () => {
    const problematicUsers = getProblematicUsers();
    if (problematicUsers.length === 0) return;

    setFixing(true);
    setFixedCount(0);

    try {
      let successCount = 0;
      for (const user of problematicUsers) {
        const success = await fixUserTrialStatus(user);
        if (success) {
          successCount++;
          setFixedCount(successCount);
        }
      }

      toast({
        title: 'Fix Complete',
        description: `Fixed ${successCount} out of ${problematicUsers.length} users`,
        variant: successCount === problematicUsers.length ? 'default' : 'destructive'
      });

      // Refresh the data
      await fetchUsers();
    } catch (error) {
      console.error('Error fixing users:', error);
      toast({
        title: 'Error',
        description: 'Failed to fix some users',
        variant: 'destructive'
      });
    } finally {
      setFixing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const problematicUsers = getProblematicUsers();

  return (
    <div className="space-y-6" data-id="k4ftlgxyw" data-path="src/components/admin/TrialStatusFixer.tsx">
      <Card data-id="hu3xx4pou" data-path="src/components/admin/TrialStatusFixer.tsx">
        <CardHeader data-id="us8ozcdsq" data-path="src/components/admin/TrialStatusFixer.tsx">
          <CardTitle className="flex items-center gap-2" data-id="fwaj3p7rv" data-path="src/components/admin/TrialStatusFixer.tsx">
            <Users className="h-5 w-5" data-id="fuygyf1oi" data-path="src/components/admin/TrialStatusFixer.tsx" />
            Trial Status Fixer
          </CardTitle>
        </CardHeader>
        <CardContent data-id="6j6ymjgfu" data-path="src/components/admin/TrialStatusFixer.tsx">
          <div className="flex items-center gap-4 mb-4" data-id="sy33ynani" data-path="src/components/admin/TrialStatusFixer.tsx">
            <Button
              onClick={fetchUsers}
              disabled={loading}
              variant="outline" data-id="9eft15nle" data-path="src/components/admin/TrialStatusFixer.tsx">

              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} data-id="l5mxbqk3s" data-path="src/components/admin/TrialStatusFixer.tsx" />
              Refresh Data
            </Button>
            
            {problematicUsers.length > 0 &&
            <Button
              onClick={fixAllProblematicUsers}
              disabled={fixing}
              variant="destructive" data-id="f2n4qufs1" data-path="src/components/admin/TrialStatusFixer.tsx">

                {fixing ?
              <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" data-id="o4nfedjql" data-path="src/components/admin/TrialStatusFixer.tsx" />
                    Fixing... ({fixedCount}/{problematicUsers.length})
                  </> :

              <>
                    Fix All ({problematicUsers.length})
                  </>
              }
              </Button>
            }
          </div>

          {problematicUsers.length > 0 ?
          <Alert className="mb-4" data-id="d6ql9fev1" data-path="src/components/admin/TrialStatusFixer.tsx">
              <AlertCircle className="h-4 w-4" data-id="w9mal4k31" data-path="src/components/admin/TrialStatusFixer.tsx" />
              <AlertDescription data-id="ypf4koaow" data-path="src/components/admin/TrialStatusFixer.tsx">
                Found {problematicUsers.length} users with incorrect trial dates that need fixing.
              </AlertDescription>
            </Alert> :

          <Alert className="mb-4" data-id="ibax8i4yt" data-path="src/components/admin/TrialStatusFixer.tsx">
              <CheckCircle2 className="h-4 w-4" data-id="n2dwk8ubq" data-path="src/components/admin/TrialStatusFixer.tsx" />
              <AlertDescription data-id="g59wxlbow" data-path="src/components/admin/TrialStatusFixer.tsx">
                All users have correct trial status. No fixes needed.
              </AlertDescription>
            </Alert>
          }

          <div className="space-y-3" data-id="nnyw8vork" data-path="src/components/admin/TrialStatusFixer.tsx">
            <h3 className="font-semibold" data-id="4qirnh618" data-path="src/components/admin/TrialStatusFixer.tsx">User Status Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-id="0kl504wyy" data-path="src/components/admin/TrialStatusFixer.tsx">
              {users.map((user) => {
                const hasProblems = problematicUsers.some((p) => p.id === user.id);
                return (
                  <Card key={user.id} className={`p-3 ${hasProblems ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`} data-id="l0eel8a9s" data-path="src/components/admin/TrialStatusFixer.tsx">
                    <div className="flex items-center justify-between" data-id="9k9ezeu8v" data-path="src/components/admin/TrialStatusFixer.tsx">
                      <div data-id="f8dlbrnqk" data-path="src/components/admin/TrialStatusFixer.tsx">
                        <div className="font-medium" data-id="zsiz8t192" data-path="src/components/admin/TrialStatusFixer.tsx">User ID: {user.user_id}</div>
                        <div className="text-sm text-gray-600" data-id="wz8bpbxkr" data-path="src/components/admin/TrialStatusFixer.tsx">
                          Tier: {user.tier_name} ({user.tier_level})
                        </div>
                        <div className="text-xs text-gray-500" data-id="qruotnhbh" data-path="src/components/admin/TrialStatusFixer.tsx">
                          Trial Start: {user.trial_start_date || 'Not set'}
                        </div>
                        <div className="text-xs text-gray-500" data-id="ih7hx765u" data-path="src/components/admin/TrialStatusFixer.tsx">
                          Trial Expires: {user.trial_expires_at || 'Not set'}
                        </div>
                      </div>
                      <Badge variant={hasProblems ? 'destructive' : 'default'} data-id="wecwvcu6g" data-path="src/components/admin/TrialStatusFixer.tsx">
                        {hasProblems ? 'Needs Fix' : 'OK'}
                      </Badge>
                    </div>
                  </Card>);

              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>);

};

export default TrialStatusFixer;